/**
 * Affiliate Portal API Service
 * 
 * Handles all API calls for the affiliate dashboard and portal.
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with auth
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isAffiliatesPage = window.location.pathname.includes('/affiliate');
      const isLoginPage = window.location.pathname === '/login';
      
      if (!isLoginPage && !isAffiliatesPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Affiliate Portal API
 */
export const affiliateAPI = {
  /**
   * Get current user's affiliate status
   */
  getStatus: () => api.get('/affiliate-portal/status'),

  /**
   * Apply to become an affiliate
   * @param {string} notes - Optional application notes
   */
  apply: (notes = null) => 
    api.post('/affiliate-portal/apply', null, { params: { notes } }),

  /**
   * Combined signup and affiliate application
   * @param {Object} data - Signup data with application notes
   * @param {string} data.email - User email
   * @param {string} data.password - User password
   * @param {string} data.full_name - User's full name
   * @param {string} data.application_notes - Why they want to be an affiliate
   */
  signup: (data) => api.post('/affiliate-portal/signup', data),

  /**
   * Get affiliate stats (clicks, signups, upgrades, commissions)
   */
  getStats: () => api.get('/affiliate-portal/my-stats'),

  /**
   * Get affiliate link
   * @param {boolean} includeUtm - Include UTM placeholders
   */
  getLink: (includeUtm = false) => 
    api.get('/affiliate-portal/my-link', { params: { include_utm: includeUtm } }),

  /**
   * Get traffic log with filtering and grouping
   * @param {Object} params - Filter parameters
   */
  getTraffic: (params = {}) => {
    const queryParams = {
      limit: params.limit || 50,
      offset: params.offset || 0,
    };

    if (params.startDate) queryParams.start_date = params.startDate;
    if (params.endDate) queryParams.end_date = params.endDate;
    if (params.sub1) queryParams.sub1 = params.sub1;
    if (params.sub2) queryParams.sub2 = params.sub2;
    if (params.sub3) queryParams.sub3 = params.sub3;
    if (params.country) queryParams.country = params.country;
    if (params.groupBy) queryParams.group_by = params.groupBy;

    return api.get('/affiliate-portal/traffic', { params: queryParams });
  },

  /**
   * Get conversions with filtering
   * @param {Object} params - Filter parameters
   */
  getConversions: (params = {}) => {
    const queryParams = {
      limit: params.limit || 50,
      offset: params.offset || 0,
    };

    if (params.startDate) queryParams.start_date = params.startDate;
    if (params.endDate) queryParams.end_date = params.endDate;
    if (params.conversionType) queryParams.conversion_type = params.conversionType;
    if (params.isPaid !== undefined && params.isPaid !== '') queryParams.is_paid = params.isPaid;

    return api.get('/affiliate-portal/conversions', { params: queryParams });
  },

  /**
   * Get payout history
   * @param {Object} params - Pagination parameters
   */
  getPayouts: (params = {}) => {
    const queryParams = {
      limit: params.limit || 50,
      offset: params.offset || 0,
    };

    return api.get('/affiliate-portal/payouts', { params: queryParams });
  },

  /**
   * Get analytics data for charts
   * @param {number} days - Number of days to analyze (7-365)
   */
  getAnalytics: (days = 30) => 
    api.get('/affiliate-portal/analytics', { params: { days } }),

  /**
   * Build custom affiliate link with sub-ID parameters and landing page
   * @param {Object} linkParams - Link parameters (sub1, sub2, sub3, page)
   */
  buildLink: (linkParams = {}) => {
    const params = {};
    if (linkParams.sub1) params.sub1 = linkParams.sub1;
    if (linkParams.sub2) params.sub2 = linkParams.sub2;
    if (linkParams.sub3) params.sub3 = linkParams.sub3;
    if (linkParams.page) params.page = linkParams.page;

    return api.get('/affiliate-portal/link-builder', { params });
  },

  /**
   * Get funnel report with grouping and filtering support
   * @param {Object} params - Report parameters
   * @param {string} params.groupBy - Group by: date, country, os, browser, sub1, sub2, sub3, device
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {string} params.sub1 - Filter by sub1
   * @param {string} params.sub2 - Filter by sub2
   * @param {string} params.sub3 - Filter by sub3
   * @param {string} params.country - Filter by country
   * @param {string} params.os - Filter by OS
   * @param {string} params.browser - Filter by browser
   * @param {string} params.device - Filter by device type
   */
  getReports: (params = {}) => {
    const queryParams = {
      group_by: params.groupBy || 'date',
    };

    if (params.startDate) queryParams.start_date = params.startDate;
    if (params.endDate) queryParams.end_date = params.endDate;
    if (params.sub1) queryParams.sub1 = params.sub1;
    if (params.sub2) queryParams.sub2 = params.sub2;
    if (params.sub3) queryParams.sub3 = params.sub3;
    if (params.country) queryParams.country = params.country;
    if (params.os) queryParams.os = params.os;
    if (params.browser) queryParams.browser = params.browser;
    if (params.device) queryParams.device = params.device;

    return api.get('/affiliate-portal/reports', { params: queryParams });
  },
};

/**
 * Public affiliate info API (no auth required)
 */
export const affiliatePublicAPI = {
  /**
   * Validate an affiliate code
   * @param {string} code - Affiliate code to validate
   */
  validateCode: (code) => 
    axios.get(`${API_BASE_URL}/aff/${code}/validate`),

  /**
   * Track a visit (called when user clicks affiliate link)
   * @param {string} code - Affiliate code
   * @param {Object} utmParams - UTM parameters
   */
  trackVisit: (code, utmParams = {}) => {
    const params = {};
    if (utmParams.source) params.utm_source = utmParams.source;
    if (utmParams.medium) params.utm_medium = utmParams.medium;
    if (utmParams.campaign) params.utm_campaign = utmParams.campaign;

    return axios.post(`${API_BASE_URL}/aff/${code}/visit`, null, { params });
  },
};

export default affiliateAPI;
